import { Dispatch, SetStateAction, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { NotificationDoc } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { t } from 'i18next';
import { produce } from 'immer';
import { Icon } from '@frontend/icons';
import { useScopedAppFlagStore } from '@frontend/scope';
import { NotificationTypesWithSubGroups } from '@frontend/settings-routing';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, Text, useTooltip } from '@frontend/design-system';
import { NotificationComponentProps } from '../historical-notification-list';
import { useNotificationContext } from '../notification-provider';
import { GroupedBaseSection, GroupedSections, NotificationContentTypeProps, Person } from '../types';
import { PopoverFilter } from './filter-chips/components/popOverFilter';
import { ForYouFilterType } from './filter-chips/constants';
import { getForYouFilters } from './filter-chips/filter-config';
import {
  CurvedDashedLine,
  getIcon,
  getNoticationTypeColor,
  getNoticationTypeTextColor,
  getTitleDisplayName,
  getUnreadCommentsText,
  RenderMoveToTimelineBlock,
} from './helper';
import {
  FallbackNotification,
  HistoricalCallIntelUnhappyCallNotification,
  HistoricalCallRecordTagNotification,
  HistoricalFaxNotification,
  HistoricalFaxTagNotification,
  HistoricalFormsNotification,
  HistoricalPaymentsNotification,
  HistoricalReviewsNotification,
  HistoricalSmsNotification,
  HistoricalSMSTagNotification,
  HistoricalVoicemailMessageNotification,
  HistoricalVoicemailTagNotification,
  HistoricalTaskNotification,
} from './individual-notifications';

export const GroupedNotificationList = ({
  groupedNotifications,
  closeModal,
  setGroupNotifications,
}: {
  groupedNotifications: GroupedSections;
  setGroupNotifications: Dispatch<SetStateAction<GroupedSections | null>>;
  closeModal?: () => void;
}) => {
  const hiddenNotificationRef = useRef<string>('');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showColorChipForYou = getFeatureFlagValue('for-you-colors-chip');
  const [forYouNotificationColors] = usePreference('ForYouNotificationColors');
  const NotificationTitle = (notificationType: string, unreadCount: number, isNotificationSectionExpanded: boolean) => {
    const notificationTypeColor = getNoticationTypeColor(
      notificationType,
      showColorChipForYou,
      forYouNotificationColors
    );
    const notificationTypeTextColor = getNoticationTypeTextColor(
      notificationType,
      showColorChipForYou,
      forYouNotificationColors
    );
    const textStyles = css`
      font-size: ${theme.spacing(1.75)};
      font-weight: ${theme.font.weight.bold};
      color: ${notificationTypeTextColor};
      line-height: ${theme.spacing(2)};
      cursor: pointer;
      text-transform: capatalize;
    `;

    const expandTextStyles = css`
      display: flex;
      align-items: center;
      gap: ${theme.spacing(0.5)};
    `;

    return (
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          padding: ${theme.spacing(1)} ${theme.spacing(2)};
          background: ${notificationTypeColor};
          position: sticky;
          top: 56px;
          z-index: 2;
        `}
      >
        <div css={{ display: 'flex', gap: '4px', color: notificationTypeTextColor }}>
          {getIcon(notificationType)}
          <Text css={textStyles}>
            {getTitleDisplayName(notificationType)} {`(${unreadCount})`}
          </Text>
        </div>
        <Text
          css={textStyles}
          onClick={() => {
            setGroupNotifications((prev: GroupedSections | null) => {
              if (!prev) return prev;
              return produce(prev, (draft: GroupedSections) => {
                draft[notificationType].isNotificationSectionExpanded = !isNotificationSectionExpanded;
              });
            });
          }}
        >
          {unreadCount !== 1 &&
            (isNotificationSectionExpanded ? (
              <div css={expandTextStyles} data-trackingid={`view-less-${notificationType.toLowerCase()}-notifications`}>
                {t('View less')}
                <Icon name='caret-up-tiny' />
              </div>
            ) : (
              <div css={expandTextStyles} data-trackingid={`view-more-${notificationType.toLowerCase()}-notifications`}>
                {t('View all')} <Icon name='caret-down-tiny' />
              </div>
            ))}
        </Text>
      </div>
    );
  };

  const NotificationComponentSwitcher = ({
    notification,
    ...props
  }: {
    notification: NotificationComponentProps;
    closeModal: () => void;
    icon?: React.ReactNode;
    lastElement?: boolean;
    isSMSThreadExpanded?: boolean;
    notificationHovered?: boolean;
  }) => {
    // We can add more notification in the individual-notification.tsx once we have them released with new UI
    switch (notification.type) {
      case NotificationType.SMS:
        return <HistoricalSmsNotification notification={notification} {...props} />;
      case NotificationType.SMS_TAG_V2:
        return <HistoricalSMSTagNotification notification={notification} {...props} />;
      case NotificationType.CALL_RECORD_TAG:
        return <HistoricalCallRecordTagNotification notification={notification} {...props} />;
      case NotificationType.PAYMENTS:
        return <HistoricalPaymentsNotification notification={notification} {...props} />;
      case NotificationType.FORMS:
        return <HistoricalFormsNotification notification={notification} {...props} />;
      case NotificationType.REVIEWS:
        return <HistoricalReviewsNotification notification={notification} {...props} />;
      case NotificationType.REVIEW_COMMENT:
        return <HistoricalReviewsNotification notification={notification} {...props} />;
      case NotificationType.FAX:
        return <HistoricalFaxNotification notification={notification} {...props} />;
      case NotificationType.FAX_TAG:
        return <HistoricalFaxTagNotification notification={notification} {...props} />;
      case NotificationType.VOICEMAIL_TAG:
        return <HistoricalVoicemailTagNotification notification={notification} {...props} />;
      case NotificationType.VOICEMAIL_MESSAGE:
        return <HistoricalVoicemailMessageNotification notification={notification} {...props} />;
      case NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL:
        return <HistoricalCallIntelUnhappyCallNotification notification={notification} {...props} />;
      case NotificationType.TASK:
        return <HistoricalTaskNotification notification={notification} {...props} />;
      default: {
        return <FallbackNotification notification={notification} {...props} />;
      }
    }
  };

  const SingleNotification = ({
    notifications,
    notificationIdHovered,
    setNotificationIdHovered,
    readNotificationAndMoveToTimeline,
    setReadNotificationAndMoveToTimeline,
    hide,
    setHide,
    notificationTypeColor,
    notificationTypeTextColor,
    notificationTrayContext,
  }: {
    notifications: NotificationDoc[];
    notificationIdHovered: string;
    setNotificationIdHovered: (val: string) => void;
    readNotificationAndMoveToTimeline: string;
    setReadNotificationAndMoveToTimeline: (val: string) => void;
    hide: boolean;
    setHide: (val: boolean) => void;
    notificationTypeColor: string;
    notificationTypeTextColor: string;
    notificationTrayContext: ReturnType<typeof useNotificationContext>;
  }) => {
    const { Tooltip, tooltipProps, triggerProps } = useTooltip();
    return (
      <>
        {notifications.map((notification) => {
          const hovered = notificationIdHovered === notification.notificationId;
          const readAndMoved = readNotificationAndMoveToTimeline === notification.notificationId;

          if (hiddenNotificationRef.current === notification.id) return null;

          return (
            <div key={notification.notificationId} css={{ position: 'relative', overflow: 'hidden' }}>
              <div
                css={{
                  position: 'relative',
                  transition: `transform 0.3s ease-in-out, max-height 0.3s ease-in-out`,
                  transform: readAndMoved ? 'translateX(100%)' : '',
                  background: readAndMoved || hovered ? theme.colors.neutral5 : '',
                  zIndex: 1,
                  maxHeight: hide && readAndMoved ? '0' : readAndMoved ? '200px' : 'auto',
                  boxShadow: '-4px 0px 4px 0px #0000000D',
                }}
                onMouseEnter={() => setNotificationIdHovered(notification.notificationId)}
                onMouseLeave={() => setNotificationIdHovered('')}
              >
                <NotificationComponentSwitcher
                  key={notification.notificationId}
                  notification={notification as NotificationComponentProps}
                  notificationHovered={hovered}
                  closeModal={() => closeModal?.()}
                />
                {hovered && (
                  <div
                    css={{ position: 'absolute', right: theme.spacing(3), top: theme.spacing(3), cursor: 'pointer' }}
                    {...triggerProps}
                  >
                    <Button
                      variant='secondary'
                      onClick={() => {
                        setReadNotificationAndMoveToTimeline(notification.notificationId);
                        setTimeout(() => setHide(true), 1000);
                        setTimeout(() => {
                          hiddenNotificationRef.current = notification.id;
                          notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
                        }, 1300);
                      }}
                      iconName='check-small'
                    />
                    {!readNotificationAndMoveToTimeline && <Tooltip {...tooltipProps}>{t('Mark as Read')}</Tooltip>}
                  </div>
                )}
              </div>
              {readAndMoved && (
                <RenderMoveToTimelineBlock
                  notificationTypeColor={notificationTypeColor}
                  notificationTypeTextColor={notificationTypeTextColor}
                />
              )}
            </div>
          );
        })}
      </>
    );
  };

  const GroupedNotification = ({
    people,
    notificationIdHovered,
    setNotificationIdHovered,
    readNotificationAndMoveToTimeline,
    setReadNotificationAndMoveToTimeline,
    hide,
    setHide,
    notificationTypeColor,
    notificationTypeTextColor,
    notificationTrayContext,
  }: {
    people: Record<string, Person>;
    notificationIdHovered: string;
    setNotificationIdHovered: (val: string) => void;
    readNotificationAndMoveToTimeline: string;
    setReadNotificationAndMoveToTimeline: (val: string) => void;
    hide: boolean;
    setHide: (val: boolean) => void;
    notificationTypeColor: string;
    notificationTypeTextColor: string;
    notificationTrayContext: ReturnType<typeof useNotificationContext>;
  }) => {
    const { Tooltip, tooltipProps, triggerProps } = useTooltip();
    const peopleEntries = useMemo(() => Object.entries(people), [people]);

    return (
      <>
        {peopleEntries.map(([userId, user]) => {
          const hovered = notificationIdHovered === userId;
          const readAndMoved = readNotificationAndMoveToTimeline === userId;

          if (user.data[0].id === hiddenNotificationRef.current) return null;

          return (
            <div
              key={userId}
              css={{
                position: 'relative',
                background: hovered ? theme.colors.neutral5 : '',
                maxHeight: hide && readAndMoved ? '0' : readAndMoved ? '200px' : 'auto',
                transition: `max-height 0.3s ease-in-out`,
                overflow: 'hidden',
              }}
              onMouseEnter={() => setNotificationIdHovered(userId)}
              onMouseLeave={() => setNotificationIdHovered('')}
            >
              {user.data.slice(0, !user.isSMSThreadExpanded ? 1 : user.data.length).map((notification, index) => (
                <div
                  key={notification.notificationId}
                  css={{
                    position: !user.isSMSThreadExpanded ? 'relative' : 'static',
                    transition: `transform 0.3s ease-in-out`,
                    transform: readAndMoved ? 'translateX(100%)' : '',
                    background: readAndMoved ? theme.colors.neutral5 : '',
                    zIndex: 1,
                    boxShadow: '-4px 0px 4px 0px #0000000D',
                  }}
                >
                  <NotificationComponentSwitcher
                    notification={notification as NotificationComponentProps}
                    lastElement={user.isSMSThreadExpanded ? index === user.data.length - 1 : index === 0}
                    isSMSThreadExpanded={user.isSMSThreadExpanded}
                    closeModal={() => closeModal?.()}
                    notificationHovered={hovered && !user.isSMSThreadExpanded}
                  />
                  {hovered && !user.isSMSThreadExpanded && (
                    <div
                      css={{
                        position: 'absolute',
                        right: theme.spacing(3),
                        top: theme.spacing(3),
                        cursor: 'pointer',
                      }}
                      {...triggerProps}
                    >
                      <Button
                        variant='secondary'
                        iconName='check-small'
                        onClick={() => {
                          setHide(false);
                          setReadNotificationAndMoveToTimeline(userId);
                          setTimeout(() => setHide(true), 1000);
                          setTimeout(() => {
                            hiddenNotificationRef.current = notification.id;
                            notificationTrayContext.markThreadNotificationsAsRead(
                              (notification.alert.payload as any)?.threadIds?.[0] || '',
                              notification.locationId
                            );
                          }, 1300);
                        }}
                      />
                      {!readNotificationAndMoveToTimeline && <Tooltip {...tooltipProps}>{t('Mark as Read')}</Tooltip>}
                    </div>
                  )}
                  {user.data.length > 1 && <CurvedDashedLine />}
                  {user.data.length > 1 && (
                    <Text
                      css={{
                        marginTop: theme.spacing(0.5),
                        marginLeft: theme.spacing(6),
                        fontSize: theme.spacing(1.5),
                        color: theme.colors.primary50,
                        cursor: 'pointer',
                        position: 'absolute',
                        bottom: theme.spacing(2),
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        // toggle SMS thread expand
                        setGroupNotifications((prev) => {
                          if (!prev) return prev;
                          return produce(prev, (draft: Record<string, GroupedBaseSection<Person>>) => {
                            if (draft[NotificationType.SMS]?.people?.[userId]) {
                              draft[NotificationType.SMS].people[userId].isSMSThreadExpanded =
                                !user.isSMSThreadExpanded;
                            }
                          });
                        });
                      }}
                    >
                      {getUnreadCommentsText(user.isSMSThreadExpanded)}
                    </Text>
                  )}
                </div>
              ))}
              {readAndMoved && (
                <RenderMoveToTimelineBlock
                  notificationTypeColor={notificationTypeColor}
                  notificationTypeTextColor={notificationTypeTextColor}
                />
              )}
            </div>
          );
        })}
      </>
    );
  };

  const NotificationContentWrapper: React.FC<
    NotificationContentTypeProps & {
      people?: Record<string, Person>;
    }
  > = ({ notificationType, data, people = {}, isSectionExpanded }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const notificationTypeColor = getNoticationTypeColor(
      notificationType,
      showColorChipForYou,
      forYouNotificationColors
    );
    const notificationTypeTextColor = getNoticationTypeTextColor(
      notificationType,
      showColorChipForYou,
      forYouNotificationColors
    );

    const [notificationIdHovered, setNotificationIdHovered] = useState('');
    const [readNotificationAndMoveToTimeline, setReadNotificationAndMoveToTimeline] = useState<string>('');
    const [hide, setHide] = useState(false);
    const notificationTrayContext = useNotificationContext();
    const isSubGrouped = NotificationTypesWithSubGroups.includes(notificationType as NotificationType);
    const visibleItems = useMemo(() => {
      if (isSubGrouped) {
        if (!people) return {};
        return Object.fromEntries(Object.entries(people).slice(0, isSectionExpanded ? Object.keys(people).length : 1));
      }

      if (!data) return [];
      return isSectionExpanded ? data : data.slice(0, 1);
    }, [isSubGrouped, isSectionExpanded, people, data]);

    return (
      <div
        ref={wrapperRef}
        css={{
          overflow: 'hidden',
          borderBottom: isSectionExpanded ? `1px solid ${theme.colors.neutral20}` : 'none',
        }}
      >
        {isSubGrouped ? (
          // This Component is for notifications with subGroups
          <GroupedNotification
            people={visibleItems as Record<string, Person>}
            notificationIdHovered={notificationIdHovered}
            setNotificationIdHovered={setNotificationIdHovered}
            readNotificationAndMoveToTimeline={readNotificationAndMoveToTimeline}
            setReadNotificationAndMoveToTimeline={setReadNotificationAndMoveToTimeline}
            hide={hide}
            setHide={setHide}
            notificationTypeColor={notificationTypeColor}
            notificationTypeTextColor={notificationTypeTextColor}
            notificationTrayContext={notificationTrayContext}
          />
        ) : (
          // This Component is for notifications without subGroups
          <SingleNotification
            notifications={visibleItems as NotificationDoc[]}
            notificationIdHovered={notificationIdHovered}
            setNotificationIdHovered={setNotificationIdHovered}
            readNotificationAndMoveToTimeline={readNotificationAndMoveToTimeline}
            setReadNotificationAndMoveToTimeline={setReadNotificationAndMoveToTimeline}
            hide={hide}
            setHide={setHide}
            notificationTypeColor={notificationTypeColor}
            notificationTypeTextColor={notificationTypeTextColor}
            notificationTrayContext={notificationTrayContext}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <div
        css={{
          display: 'flex',
          gap: theme.spacing(1),
          margin: `${theme.spacing(1)} ${theme.spacing(2)} ${theme.spacing(3)}`,
        }}
      >
        {getForYouFilters(showColorChipForYou).map((filter: ForYouFilterType) => {
          return <PopoverFilter filter={filter} />;
        })}
      </div>
      {Object.keys(groupedNotifications as GroupedSections).map((notificationType: string) => {
        const notificationData: GroupedBaseSection | GroupedBaseSection<Person> =
          groupedNotifications?.[notificationType];

        return (
          <div key={notificationType}>
            {NotificationTitle(
              notificationType,
              notificationData?.unreadCount,
              notificationData.isNotificationSectionExpanded
            )}
            <NotificationContentWrapper
              notificationType={notificationType}
              // People represents data for sections with subgroups
              people={(notificationData as GroupedBaseSection<Person>).people}
              // Data represents the data for sections without subgroups
              data={notificationData.data || []}
              isSectionExpanded={notificationData.isNotificationSectionExpanded}
            />
          </div>
        );
      })}
    </div>
  );
};
